import React, { useState } from "react";
import { Check, Bell, ChevronRight, X, MapPin, CalendarCheck2, Loader2 } from "lucide-react";
import { PROPERTIES, BOOKING_URL } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { submitEnquiry } from "../lib/api";

const statusStyle = (status) => {
  if (status === "Now Available") return "bg-emerald-500 text-white";
  if (status === "Opening Soon") return "bg-[var(--forge-gold)] text-forge-navy";
  return "bg-forge-navy text-white";
};

export default function OurAccommodation() {
  const { toast } = useToast();
  const [open, setOpen] = useState(null); // property object
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
      toast({ title: "Please enter a valid email" });
      return;
    }
    setBusy(true);
    try {
      const kind = open?.status === "Opening Soon" ? "register_interest" : "notify_me";
      await submitEnquiry({
        kind,
        email,
        property: open?.name
      });
      toast({
        title: "You're on the list!",
        description: `We'll let you know as soon as ${open?.name} is bookable.`
      });
      setEmail("");
      setOpen(null);
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again in a moment or email us directly."
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="accommodation" className="py-24 px-6 bg-forge-off">
      <div className="max-w-6xl mx-auto text-center mb-14">
        <div className="text-[11px] tracking-[0.3em] uppercase text-forge-gold">Our Stays</div>
        <h2 className="font-serif text-4xl md:text-5xl text-forge-navy mt-2">Places to Stay Across Anglesey</h2>
        <hr className="forge-divider" />
        <p className="text-forge-navy/70 max-w-2xl mx-auto">
          Book our entire Cymyran Terrace townhouses or the Newry Street townhouse now —
          with more on the way.
        </p>
      </div>

      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
        {PROPERTIES.map((p) => (
          <article key={p.key} className="group bg-white border border-black/5 overflow-hidden flex flex-col">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={p.image} alt={p.name}
                   className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute top-4 left-4">
                <span className={`inline-block ${statusStyle(p.status)} text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 font-semibold`}>
                  {p.status}
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 text-white text-xs tracking-[0.16em] uppercase">
                <MapPin size={13} className="text-forge-gold" /> {p.location}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="p-7 flex-1 flex flex-col">
              <h3 className="font-serif text-2xl text-forge-navy leading-tight">{p.name}</h3>
              <div className="text-sm text-forge-navy/60 mt-1">{p.subtitle}</div>
              <p className="text-forge-navy/75 mt-4 text-sm leading-relaxed">{p.description}</p>

              <ul className="mt-5 space-y-2">
                {p.highlights.map((h) => (
                  <li key={h} className="flex items-center gap-2 text-sm text-forge-navy/80">
                    <span className="w-5 h-5 rounded-full bg-forge-cream flex items-center justify-center shrink-0">
                      <Check size={12} className="text-forge-navy" strokeWidth={2.5} />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>

              <div className="mt-7 pt-5 border-t border-black/5">
                {p.status === "Now Available" ? (
                  <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="btn-gold w-full">
                    <CalendarCheck2 size={14} /> {p.cta}
                  </a>
                ) : (
                  <button onClick={() => setOpen(p)} className="btn-navy w-full">
                    <Bell size={14} /> {p.cta} <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white w-full max-w-md p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(null)} aria-label="Close" className="absolute top-4 right-4 text-forge-navy/60 hover:text-forge-navy">
              <X size={20} />
            </button>
            <div className="text-[11px] tracking-[0.3em] uppercase text-forge-gold">Notify Me</div>
            <h4 className="font-serif text-2xl text-forge-navy mt-2">{open.name}</h4>
            <p className="text-sm text-forge-navy/70 mt-2">
              Drop your email and we'll let you know the moment {open.name} is bookable.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="your@email.com"
                     className="w-full bg-forge-off px-4 py-3 border border-black/10 focus:border-[var(--forge-gold)] outline-none text-forge-navy" />
              <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-70">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {busy ? "Sending…" : "Keep Me Posted"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
